import React, { useCallback, useEffect, useRef } from 'react';
import { Control, useController } from 'react-hook-form';
import ErrorLabel from './ErrorLabel';
import { motion } from 'motion/react';

interface OtpInputProps {
  digits: number;
  showMiddleDivider?: boolean;
  value?: string;
  onChange?: (value: string) => void;
}

export default function OtpInput({
  digits,
  showMiddleDivider = true,
  value,
  onChange,
}: OtpInputProps) {
  const refs = useRef<HTMLInputElement[]>([]);

  const getAggregateValue = useCallback(() => {
    return refs.current.reduce((acc, input) => {
      return acc + input.value;
    }, '');
  }, []);

  const handleOnChangeInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e?.target?.value?.replace(/\D/g, '').slice(-1);
      const index = Number(e.target.dataset.index);

      e.target.value = value;

      onChange?.(getAggregateValue());

      if (value.length === 1 && index < digits - 1) {
        return refs.current[index + 1].focus();
      }

      if (value.length === 0 && index > 0) {
        return refs.current[index - 1].focus();
      }
    },
    [digits, getAggregateValue, onChange]
  );

  const handleOnKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !e.currentTarget.value) {
      const index = Number(e.currentTarget.dataset.index);
      const newIndex = index - 1;

      if (index === 0) return;
      refs.current[newIndex].focus();
    }
  }, []);

  const handleTextEvents = useCallback(
    (text: string) => {
      const digitsArray = (text.match(/\d/g) || [])?.slice(0, digits);

      digitsArray.forEach((digit, index) => {
        refs.current[index].value = digit;
      });

      onChange?.(getAggregateValue());
    },
    [digits, getAggregateValue, onChange]
  );

  const handleOnPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const text = e.clipboardData.getData('text');
      handleTextEvents(text);
    },
    [handleTextEvents]
  );

  const handleOnDrop = useCallback(
    (e: React.DragEvent<HTMLInputElement>) => {
      e.preventDefault();
      const text = e.dataTransfer.getData('text');
      handleTextEvents(text);
    },
    [handleTextEvents]
  );

  useEffect(() => {
    if (value) {
      const digitsArray = value.match(/\d/g) || [];
      digitsArray.forEach((digit, index) => {
        refs.current[index].value = digit;
      });
    }
  }, [value]);

  return (
    <div className="flex h-16 w-16 items-center justify-between gap-4">
      {Array.from({ length: digits }).map((_, index) => (
        <React.Fragment key={index}>
          <motion.input
            initial={{ opacity: 0, translateY: 10 }}
            animate={{ opacity: 1, translateY: 0 }}
            transition={{ duration: 0.3, bounce: 0.2, delay: index * 0.05 }}
            type="number"
            className="bg-cx-black aspect-square h-full w-full rounded-xs text-center text-2xl font-semibold outline-hidden selection:bg-white/5"
            ref={(el) => {
              if (el) refs.current[index] = el;
            }}
            onChange={handleOnChangeInput}
            data-index={index}
            onFocus={(e) => e.target.select()}
            maxLength={1}
            onKeyDown={handleOnKeyDown}
            onPaste={handleOnPaste}
            onDrop={handleOnDrop}
          />
          {showMiddleDivider && index === Math.floor(digits / 2) - 1 && (
            <div className="aspect-square w-6 rounded-full bg-white/25" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

interface OtpInputFormProps extends OtpInputProps {
  name: string;
  control: Control;
}

export function OtpInputForm({ control, name, ...rest }: OtpInputFormProps) {
  const { field, fieldState } = useController({ control, name });

  return (
    <div className="flex flex-col">
      <input type="hidden" {...field} />

      <OtpInput {...rest} value={field.value} onChange={field.onChange} />
      {fieldState?.error && <ErrorLabel>{fieldState?.error?.message}</ErrorLabel>}
    </div>
  );
}
